/**
 * /app/services — agency commercial catalog (Task 046 + Task 047).
 *
 * Composition root only. The page loads services + packages via
 * `useServicesPackages`; all section/form/composition logic lives under
 * `components/services-packages/`. Backend behavior is unchanged.
 *
 * Information architecture (Task 151-B): the **packages cards grid
 * (`PackagesGrid`) is the primary first-screen surface**, with a
 * create/edit drawer for the real package-management flow. The
 * services/packages catalog lists stay demoted into a secondary,
 * collapsible "manage" area (`ManageCatalogsSection`) — collapsed in the
 * steady state, opened automatically on first run (empty catalog) so the
 * create-first-service/package path stays reachable.
 *
 * Permission UX (load-bearing guard is RLS):
 *   - Read: any agency-side actor or a client-side user with
 *     `contract.read`.
 *   - Manage: `billing.subscription.manage` (owner /
 *     marketing_manager / finance_manager).
 */

import { useMeContext } from '../context/MeContextProvider';
import { hasAnyPermission, PERM } from '../permissions';
import { FoundationDefaultsSection } from '../components/services-packages/FoundationDefaultsSection';
import { ManageCatalogsSection } from '../components/services-packages/ManageCatalogsSection';
import { PackagesGrid } from '../components/services-packages/PackagesGrid';
import { PackagesSection } from '../components/services-packages/PackagesSection';
import { ServicesSection } from '../components/services-packages/ServicesSection';
import { useServicesPackages } from '../components/services-packages/useServicesPackages';
import { ErrorState, LoadingState } from '../components/states';

export function AgencyServicesPage() {
  const { me } = useMeContext();
  const { state, refresh } = useServicesPackages();

  const canManage =
    !!me && !me.system_admin && hasAnyPermission(me.permissions, PERM.BILLING_SUBSCRIPTION_MANAGE);

  if (state.status === 'loading') return <LoadingState title="جاري تحميل الخدمات والباقات…" />;
  if (state.status === 'error')
    return <ErrorState title="تعذر تحميل الخدمات والباقات" error={state.error} onRetry={refresh} />;

  return (
    <div className="stack-md">
      <div>
        <h1 className="h-page">
          الخدمات <em className="serif italic">والباقات</em>
        </h1>
        <p className="body" style={{ marginTop: 6, color: 'var(--ink-2)' }}>
          عرّف باقات الخدمة التي تقدّمها لعملائك وأنشئ باقة جديدة أو عدّل باقة قائمة. الإدارة
          التفصيلية للخدمات في القسم القابل للطي بالأسفل.
        </p>
      </div>

      <PackagesGrid
        services={state.services}
        packages={state.packages}
        canManage={canManage}
        onSaved={refresh}
      />

      <ManageCatalogsSection
        defaultOpen={state.services.length === 0 || state.packages.length === 0}
      >
        <FoundationDefaultsSection />

        <ServicesSection services={state.services} canManage={canManage} onSaved={refresh} />

        <PackagesSection packages={state.packages} canManage={canManage} onSaved={refresh} />
      </ManageCatalogsSection>
    </div>
  );
}
